import { useContext, useEffect, useState, useCallback } from "react"
import { VibratoClientContext } from "./contexts"
import CitySearchModal from "./CitySearchModal"
import CategorySearchModal from "./CategorySearchModal"
import { useParams, Link } from "react-router-dom"
import {
  Building,
  MapPin,
  Page,
  Shield,
  ArrowLeft,
  Check,
  Plus,
  Minus,
  ArrowUpRight,
  WarningCircle,
  Folder,
  Xmark
} from "iconoir-react"
import { countryCodeInfo } from "@/lib/locales"
import { settings } from "@/settings"
import { CategorySearchResult } from "@/lib/vibrato-api"

// Country code options based on frontend locales
const COUNTRY_CODE_OPTIONS = Object.entries(countryCodeInfo)
  .map(([key, info]) => ({
    code: info.code,
    name: info.displayName,
    uniqueKey: key
  }))
  .sort((a, b) => a.name.localeCompare(b.name))

export default function EditVerifiedBusiness() {
  const { uuid } = useParams()
  const vibratoClient = useContext(VibratoClientContext)

  const [showCitySearch, setShowCitySearch] = useState(false)
  const [selectedCity, setSelectedCity] = useState<any>(null)
  const [showCategorySearch, setShowCategorySearch] = useState(false)
  const [categorySearchMode, setCategorySearchMode] = useState<"primary" | "additional">("primary")
  const [selectedPrimaryCategory, setSelectedPrimaryCategory] = useState<CategorySearchResult | null>(null)
  const [selectedCategories, setSelectedCategories] = useState<CategorySearchResult[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [errors, setErrors] = useState<any>({})
  const [showSuccess, setShowSuccess] = useState(false)

  // Form state with stable IDs for dynamic fields
  const [form, setForm] = useState({
    name: "",
    slug: "", // Add slug to form state
    phone: "",
    country_code: "",
    website_url: "",
    address: "",
    address2: "",
    zip: "",
    short_description: "",
    long_description: "",
    products: [] as { id: string; value: string }[],
    advantages: [] as { id: string; value: string }[],
    locations_serviced: [] as { id: string; value: string }[],
    common_issues: [] as { id: string; value: string }[]
  })

  // Client-side validation errors
  const [formErrors, setFormErrors] = useState<{
    name?: string
    phone?: string
    country_code?: string
    address?: string
    zip?: string
    city?: string
  }>({})

  // Helper to generate unique IDs
  const generateId = () => Math.random().toString(36).substr(2, 9)

  const [originalData, setOriginalData] = useState<any>(null)

  useEffect(() => {
    const fetchBusiness = async () => {
      if (!vibratoClient || !uuid) return
      try {
        setIsLoading(true)
        const business = await vibratoClient.getVerifiedBusiness(uuid)
        setOriginalData(business)
        setForm({
          name: business.name || "",
          slug: business.slug || "", // Set slug from fetched data
          phone: business.phone || "",
          country_code: business.country_code || "",
          website_url: business.website_url || "",
          address: business.address || "",
          address2: business.address2 || "",
          zip: business.zip || "",
          short_description: business.short_description || "",
          long_description: business.long_description || "",
          products: (business.products || []).map((item: string) => ({ id: generateId(), value: item })),
          advantages: (business.advantages || []).map((item: string) => ({ id: generateId(), value: item })),
          locations_serviced: (business.locations_serviced || []).map((item: string) => ({
            id: generateId(),
            value: item
          })),
          common_issues: (business.common_issues || []).map((item: string) => ({ id: generateId(), value: item }))
        })
        if (business.city_name && business.state) {
          setSelectedCity({
            uuid: business.city_uuid,
            name: business.city_name,
            state: business.state
          })
        }

        // Set primary category
        if (business.primary_category) {
          setSelectedPrimaryCategory(business.primary_category)
        }

        // Set additional categories
        if (business.categories && Array.isArray(business.categories)) {
          setSelectedCategories(business.categories)
        }
      } catch (error) {
        console.error("Failed to fetch business:", error)
        alert("Failed to load business details.")
      } finally {
        setIsLoading(false)
      }
    }

    fetchBusiness()
  }, [vibratoClient, uuid])

  const validateForm = () => {
    const newErrors: {
      name?: string
      phone?: string
      country_code?: string
      address?: string
      zip?: string
      city?: string
    } = {}
    if (!form.name.trim()) {
      newErrors.name = "Business name is required."
    }
    if (!form.phone.trim()) {
      newErrors.phone = "Phone number is required."
    }
    if (!form.country_code) {
      newErrors.country_code = "Country code is required."
    }
    if (!form.address.trim()) {
      newErrors.address = "Address is required."
    }
    if (!form.zip.trim()) {
      newErrors.zip = "Zip code is required."
    }
    if (!selectedCity) {
      newErrors.city = "City is required."
    }
    setFormErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const update = (k: keyof typeof form, v: any) => {
    setForm((f) => ({ ...f, [k]: v }))
    // Clear validation error for the field when user types/selects
    if (formErrors[k as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [k]: undefined }))
    }
  }

  // Memoized onChange handlers for dynamic lists
  const onProductsChange = useCallback((newItems: { id: string; value: string }[]) => {
    setForm((f) => ({ ...f, products: newItems }))
  }, [])

  const onAdvantagesChange = useCallback((newItems: { id: string; value: string }[]) => {
    setForm((f) => ({ ...f, advantages: newItems }))
  }, [])

  const onLocationsChange = useCallback((newItems: { id: string; value: string }[]) => {
    setForm((f) => ({ ...f, locations_serviced: newItems }))
  }, [])

  const onIssuesChange = useCallback((newItems: { id: string; value: string }[]) => {
    setForm((f) => ({ ...f, common_issues: newItems }))
  }, [])

  // Category handling functions
  const handleCategorySelect = (category: CategorySearchResult) => {
    if (categorySearchMode === "primary") {
      setSelectedPrimaryCategory(category)
      setShowCategorySearch(false)
    } else {
      // For additional categories, add if not already selected
      if (!selectedCategories.some((cat) => cat.uuid === category.uuid)) {
        setSelectedCategories((prev) => [...prev, category])
      }
    }
  }

  const handleRemoveCategory = (categoryUuid: string) => {
    setSelectedCategories((prev) => prev.filter((cat) => cat.uuid !== categoryUuid))
  }

  const openPrimaryCategorySearch = () => {
    setCategorySearchMode("primary")
    setShowCategorySearch(true)
  }

  const openAdditionalCategorySearch = () => {
    setCategorySearchMode("additional")
    setShowCategorySearch(true)
  }

  // Get excluded categories for search modal (primary + already selected additional)
  const getExcludedCategories = () => {
    const excluded = [...selectedCategories]
    if (selectedPrimaryCategory && categorySearchMode === "additional") {
      excluded.push(selectedPrimaryCategory)
    }
    return excluded
  }

  const submit = async () => {
    if (!vibratoClient || !uuid) return

    if (!validateForm()) {
      return // Don't submit if form is invalid
    }

    try {
      setIsSubmitting(true)
      setErrors({}) // Clear previous backend errors
      const payload: any = {
        ...form,
        // Convert object arrays back to string arrays
        products: form.products.map((item) => item.value).filter(Boolean),
        advantages: form.advantages.map((item) => item.value).filter(Boolean),
        locations_serviced: form.locations_serviced.map((item) => item.value).filter(Boolean),
        common_issues: form.common_issues.map((item) => item.value).filter(Boolean)
      }

      // Add city UUID if selected
      if (selectedCity?.uuid) {
        payload.city_uuid = selectedCity.uuid
      }

      // Add category UUIDs if selected
      if (selectedPrimaryCategory?.uuid) {
        payload.primary_category_uuid = selectedPrimaryCategory.uuid
      }

      if (selectedCategories.length > 0) {
        payload.categories_uuids = selectedCategories.map((cat) => cat.uuid)
      }

      await vibratoClient.updateVerifiedBusiness(uuid, payload)
      setErrors({}) // Clear any existing errors
      setShowSuccess(true)
      // Auto-hide success message after 3 seconds
      setTimeout(() => setShowSuccess(false), 3000)
    } catch (e: any) {
      console.error("Failed to update business", e)
      if (e.data && typeof e.data === "object") {
        setErrors(e.data)
      } else {
        alert("Failed to update business. Please try again.")
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  const hasBackendErrors = Object.keys(errors).length > 0
  const hasFrontendErrors = Object.values(formErrors).some((err) => err)

  // Helper component to display field errors
  const FieldError = ({ fieldName }: { fieldName: string }) => {
    const fieldErrors = errors[fieldName]
    if (!fieldErrors || !Array.isArray(fieldErrors)) return null
    return (
      <div className="text-red-600 text-sm mt-1">
        {fieldErrors.map((error: string, index: number) => (
          <div key={index}>{error}</div>
        ))}
      </div>
    )
  }

  // Dynamic list field component
  const DynamicListField = useCallback(
    ({
      label,
      fieldName,
      placeholder,
      items,
      onChange,
      description
    }: {
      label: string
      fieldName: keyof typeof form
      placeholder: string
      items: { id: string; value: string }[]
      onChange: (newItems: { id: string; value: string }[]) => void
      description?: string
    }) => {
      const addItem = useCallback(() => {
        onChange([...items, { id: generateId(), value: "" }])
      }, [items, onChange])

      const removeItem = useCallback(
        (id: string) => {
          const newItems = items.filter((item) => item.id !== id)
          onChange(newItems)
        },
        [items, onChange]
      )

      const updateItem = useCallback(
        (id: string, value: string) => {
          const newItems = items.map((item) => (item.id === id ? { ...item, value } : item))
          onChange(newItems)
        },
        [items, onChange]
      )

      return (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="block text-sm font-medium text-gray-700">{label}</label>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-1 px-3 py-1 text-sm text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>

          {description && <p className="text-xs text-gray-500 mt-0.5 mb-3">{description}</p>}

          <div className="space-y-2">
            {items.length === 0 ? (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                <p className="text-gray-500 text-sm mb-2">No {label.toLowerCase()} added yet</p>
                <button
                  type="button"
                  onClick={addItem}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add {label.slice(0, -1)}
                </button>
              </div>
            ) : (
              items.map((item) => (
                <div key={item.id} className="flex gap-2">
                  <input
                    className={`flex-1 px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                      errors[fieldName] ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                    }`}
                    placeholder={placeholder}
                    value={item.value}
                    onChange={(e) => updateItem(item.id, e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => removeItem(item.id)}
                    className="p-3 text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-xl transition-colors"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                </div>
              ))
            )}
          </div>

          <FieldError fieldName={fieldName as string} />
        </div>
      )
    },
    []
  )

  if (isLoading) {
    return (
      <div className="h-full w-full p-6 md:p-8 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600" />
      </div>
    )
  }

  return (
    <div className="h-full w-full p-6 md:p-8 flex flex-col gap-8">
      <CitySearchModal
        show={showCitySearch}
        onClose={() => setShowCitySearch(false)}
        onSelect={(city) => setSelectedCity(city)}
      />

      <CategorySearchModal
        show={showCategorySearch}
        onClose={() => setShowCategorySearch(false)}
        onSelect={handleCategorySelect}
        multiSelect={categorySearchMode === "additional"}
        selectedCategories={categorySearchMode === "additional" ? selectedCategories : []}
        excludeCategories={getExcludedCategories()}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/business"
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="p-3 bg-purple-100 rounded-xl">
            <Building width="27" className="text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Edit Business Profile</h1>
            <p className="text-gray-600">
              Update your public Vibrato business profile. This is what customers see when they search for businesses on
              Vibrato and when people search for common support issues on Google.
            </p>
          </div>
        </div>
      </div>

      {originalData?.slug && (
        <>
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 p-6 rounded-2xl">
            <div className="flex items-start gap-4">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ArrowUpRight className="w-6 h-6 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Public Business Page</h3>
                <p className="text-gray-700 mb-3 max-w-xl">
                  View your public business page. This is what customers will see.
                </p>
                <a
                  href={`${settings.VIBRATO_CONTENT_URL}c/biz/${originalData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-gray-500 hover:text-gray-600 block break-all underline"
                >
                  {settings.VIBRATO_CONTENT_URL}c/biz/{originalData.slug}
                </a>
                <div className="border-t border-blue-200 my-4" />
                <a
                  href={`${settings.VIBRATO_CONTENT_URL}c/biz/${originalData.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-600 hover:text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-lg transition-colors font-medium"
                >
                  View Public Page <ArrowUpRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
          <div className="bg-orange-50 border border-orange-200 p-4 rounded-2xl">
            <div className="flex items-start gap-3 text-sm text-orange-600">
              <WarningCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>
                Please note: Changes to your business information may take up to an hour to be reflected on the public
                page due to caching.
              </span>
            </div>
          </div>
        </>
      )}

      {/* Basic Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Building className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Basic Information</h2>
          </div>
          <p className="text-gray-600 text-sm">Core business details and contact information</p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Business Name</label>
              <input
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.name || formErrors.name
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="Enter business name"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
              />
              <FieldError fieldName="name" />
              {formErrors.name && <div className="text-red-600 text-sm mt-1">{formErrors.name}</div>}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Business URL Slug</label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                This is the unique identifier in your public URL:{" "}
                <code className="text-xs bg-gray-100 p-1 rounded">
                  {settings.VIBRATO_CONTENT_URL}c/biz/{form.slug}
                </code>
                . To change this, please{" "}
                <a
                  href="mailto:contact@getvibrato.com?subject=Request to change business slug"
                  className="text-purple-600 hover:underline"
                >
                  contact support
                </a>
                .
              </p>
              <input
                disabled
                className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-xl text-gray-500 cursor-not-allowed"
                value={form.slug}
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Phone Number</label>
              <input
                type="tel"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.phone || formErrors.phone
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="(555) 123-4567"
                value={form.phone}
                onChange={(e) => update("phone", e.target.value)}
              />
              <FieldError fieldName="phone" />
              {formErrors.phone && <div className="text-red-600 text-sm mt-1">{formErrors.phone}</div>}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Country Code</label>
              <select
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all bg-white ${
                  errors.country_code || formErrors.country_code
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                value={form.country_code}
                onChange={(e) => update("country_code", e.target.value)}
              >
                <option value="">Select country code</option>
                {COUNTRY_CODE_OPTIONS.map((country) => (
                  <option key={country.uniqueKey} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </select>
              <FieldError fieldName="country_code" />
              {formErrors.country_code && <div className="text-red-600 text-sm mt-1">{formErrors.country_code}</div>}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Website URL</label>
              <input
                type="url"
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.website_url ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="https://example.com"
                value={form.website_url}
                onChange={(e) => update("website_url", e.target.value)}
              />
              <FieldError fieldName="website_url" />
            </div>
          </div>
        </div>
      </div>

      {/* Location Information */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-4">
            <MapPin className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Location Information</h2>
          </div>
          <button
            onClick={() => setShowCitySearch(true)}
            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2"
          >
            <MapPin className="w-5 h-5" />
            {selectedCity ? "Change City" : "Select City"}
          </button>
          {formErrors.city && <div className="text-red-600 text-sm mt-2">{formErrors.city}</div>}
        </div>

        {selectedCity && (
          <div className="p-6">
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <MapPin className="w-5 h-5 text-green-600" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 text-lg">{selectedCity.name}</div>
                  <div className="text-gray-600">{selectedCity.state}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-6">
          <div className="grid grid-cols-1 gap-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 1</label>
              <input
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.address || formErrors.address
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="123 Main Street"
                value={form.address}
                onChange={(e) => update("address", e.target.value)}
              />
              <FieldError fieldName="address" />
              {formErrors.address && <div className="text-red-600 text-sm mt-1">{formErrors.address}</div>}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Address Line 2 (Optional)</label>
              <input
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.address2 ? "border-red-300 focus:ring-red-500" : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="Suite 200, Apt 4B, etc."
                value={form.address2}
                onChange={(e) => update("address2", e.target.value)}
              />
              <FieldError fieldName="address2" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Zip Code</label>
              <input
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all ${
                  errors.zip || formErrors.zip
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                placeholder="94102"
                value={form.zip}
                onChange={(e) => update("zip", e.target.value)}
              />
              <FieldError fieldName="zip" />
              {formErrors.zip && <div className="text-red-600 text-sm mt-1">{formErrors.zip}</div>}
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">City</label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                To change the city, use the "Change City" button above.
              </p>
              <input
                disabled
                className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-xl text-gray-500 cursor-not-allowed"
                value={selectedCity?.name || ""}
                placeholder="Select a city above"
              />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">State</label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                To change the state, use the "Change City" button above.
              </p>
              <input
                disabled
                className="w-full px-4 py-3 border border-gray-200 bg-gray-50 rounded-xl text-gray-500 cursor-not-allowed"
                value={selectedCity?.state || ""}
                placeholder="Select a city above"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Category Selection */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Folder className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Business Categories</h2>
          </div>
          <p className="text-gray-600 text-sm">Categorize your business to help customers find you</p>
        </div>

        <div className="p-6 space-y-6">
          {/* Primary Category */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Primary Category</label>
                <p className="text-xs text-gray-500">The main category that best describes your business</p>
              </div>
              <button
                onClick={openPrimaryCategorySearch}
                className="px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg font-medium shadow hover:shadow-lg transition-all duration-300 flex items-center gap-2 self-start sm:self-auto"
              >
                <Folder className="w-4 h-4" />
                {selectedPrimaryCategory ? "Change Primary" : "Select Primary"}
              </button>
            </div>

            {selectedPrimaryCategory && (
              <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900 text-lg">{selectedPrimaryCategory.name}</div>
                    {selectedPrimaryCategory.description && (
                      <div className="text-gray-600 mt-1 line-clamp-2">{selectedPrimaryCategory.description}</div>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedPrimaryCategory(null)}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Xmark className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Additional Categories */}
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Categories</label>
                <p className="text-xs text-gray-500">Other categories that apply to your business (optional)</p>
              </div>
              <button
                onClick={openAdditionalCategorySearch}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2 self-start sm:self-auto"
              >
                <Plus className="w-4 h-4" />
                Add Category
              </button>
            </div>

            {selectedCategories.length > 0 ? (
              <div className="grid grid-cols-1 gap-3">
                {selectedCategories.map((category) => (
                  <div
                    key={category.uuid}
                    className="bg-gray-50 border border-gray-200 rounded-xl p-3 flex items-start justify-between gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900">{category.name}</div>
                      {category.description && (
                        <div className="text-sm text-gray-600 mt-1 line-clamp-2">{category.description}</div>
                      )}
                    </div>
                    <button
                      onClick={() => handleRemoveCategory(category.uuid)}
                      className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Xmark className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-xl">
                <Folder className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500 text-sm mb-2">No additional categories selected</p>
                <button
                  onClick={openAdditionalCategorySearch}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm text-purple-600 hover:text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  Add Category
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Business Descriptions */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Page className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Business Descriptions</h2>
          </div>
          <p className="text-gray-600 text-sm">Describe your business for customers</p>
        </div>
        <div className="p-6">
          <div className="space-y-6">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Short Description</label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                A concise summary that appears at the top of your business profile.
              </p>
              <textarea
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none ${
                  errors.short_description
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                rows={3}
                placeholder="Brief description of your business..."
                value={form.short_description}
                onChange={(e) => update("short_description", e.target.value)}
              />
              <FieldError fieldName="short_description" />
            </div>
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Long Description</label>
              <p className="text-xs text-gray-500 mt-0.5 mb-3">
                A detailed description for the 'About' section of your profile.
              </p>
              <textarea
                className={`w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:border-transparent transition-all resize-none ${
                  errors.long_description
                    ? "border-red-300 focus:ring-red-500"
                    : "border-gray-300 focus:ring-purple-500"
                }`}
                rows={5}
                placeholder="Detailed description of your business, services, and what makes you unique..."
                value={form.long_description}
                onChange={(e) => update("long_description", e.target.value)}
              />
              <FieldError fieldName="long_description" />
            </div>
          </div>
        </div>
      </div>

      {/* Business Data */}
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <Shield className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-semibold text-gray-900">Business Data</h2>
          </div>
          <p className="text-gray-600 text-sm">Additional business information (comma-separated lists)</p>
        </div>
        <div className="p-6">
          <div className="space-y-8">
            <DynamicListField
              label="Products/Services"
              fieldName="products"
              placeholder="Enter a product or service"
              items={form.products}
              onChange={onProductsChange}
              description="List the primary products or services your business offers."
            />
            <DynamicListField
              label="Advantages"
              fieldName="advantages"
              placeholder="Enter a competitive advantage"
              items={form.advantages}
              onChange={onAdvantagesChange}
              description="What makes your business stand out? (e.g., '24/7 Support', 'Free Consultations')."
            />
            <DynamicListField
              label="Locations Serviced"
              fieldName="locations_serviced"
              placeholder="Enter a location or area you serve"
              items={form.locations_serviced}
              onChange={onLocationsChange}
              description="List the cities, neighborhoods, or regions you serve. This will appear as a list on your profile."
            />
            <DynamicListField
              label="Common Issues"
              fieldName="common_issues"
              placeholder="Enter a common customer issue or request"
              items={form.common_issues}
              onChange={onIssuesChange}
              description="List common problems your customers face. These will be shown as quick actions for customers to start a call about (e.g., 'Billing question', 'Technical support')."
            />
          </div>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex flex-col items-center gap-4 pb-8">
        {/* Success Message */}
        {showSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 max-w-md animate-fade-in">
            <div className="flex items-center gap-2 text-green-800 font-medium mb-1">
              <Check className="w-5 h-5" />
              Changes Saved Successfully!
            </div>
            <p className="text-green-700 text-sm">Your business information has been updated and is now live.</p>
          </div>
        )}

        <button
          disabled={isSubmitting || Object.values(formErrors).some((err) => err)}
          onClick={submit}
          className="px-8 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center gap-3 text-lg"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              Saving Changes...
            </>
          ) : (
            <>
              <Check className="w-6 h-6" />
              Save Changes
            </>
          )}
        </button>

        {(hasBackendErrors || hasFrontendErrors) && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 max-w-md">
            <div className="flex items-center gap-2 text-red-800 font-medium mb-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              Please fix the errors below
            </div>
            <p className="text-red-700 text-sm">
              Check the form fields above for validation errors and correct them before saving.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
